import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useContent } from '../context/ContentContext';

const slugify = (label) => label.toLowerCase().replace(/\s+/g, '-');

const navLinkClass = (isActive) =>
  isActive ? 'text-silver-400' : 'text-white/70 hover:text-silver-400';

const DesktopDropdown = ({ link, isOpen, onOpen, onClose, location }) => (
  <div className="relative" onMouseEnter={onOpen} onMouseLeave={onClose}>
    <button
      type="button"
      aria-haspopup="menu"
      aria-expanded={isOpen}
      className={`nav-link text-sm font-medium tracking-wider uppercase transition-all duration-300 flex items-center gap-1 ${navLinkClass(location.pathname.includes(link.basePath))}`}
      data-testid={`nav-dropdown-${slugify(link.label)}`}
    >
      {link.label}
      <ChevronDown size={16} className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
    </button>

    <div
      className={`absolute top-full left-0 mt-2 w-56 bg-black/95 backdrop-blur-md border border-silver-400/20 rounded-lg shadow-lg overflow-hidden transition-all duration-300 ${
        isOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
      }`}
    >
      {link.items.map((item) => (
        <Link
          key={item.path}
          to={item.path}
          className={`block px-4 py-3 text-sm font-medium tracking-wider uppercase transition-all duration-300 ${
            location.pathname + location.hash === item.path
              ? 'bg-silver-400/20 text-silver-400'
              : 'text-white/70 hover:bg-silver-400/10 hover:text-silver-400'
          }`}
          data-testid={`nav-item-${slugify(item.label)}`}
        >
          {item.label}
        </Link>
      ))}
    </div>
  </div>
);

const MobileDropdown = ({ link, isOpen, onToggle, onNavigate, location }) => (
  <div>
    <button
      type="button"
      aria-haspopup="menu"
      aria-expanded={isOpen}
      onClick={onToggle}
      className={`flex items-center justify-between w-full text-lg font-medium tracking-wider uppercase transition-all duration-300 ${navLinkClass(location.pathname.includes(link.basePath))}`}
    >
      {link.label}
      <ChevronDown size={20} className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
    </button>
    {isOpen && (
      <div className="ml-4 mt-2 space-y-2">
        {link.items.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            onClick={onNavigate}
            className={`block text-base font-medium tracking-wider uppercase transition-all duration-300 ${navLinkClass(location.pathname + location.hash === item.path)}`}
          >
            {item.label}
          </Link>
        ))}
      </div>
    )}
  </div>
);

const buildNavLinks = (roster) => [
  { path: '/', label: 'Home' },
  {
    label: 'Artists',
    dropdown: true,
    basePath: '/artists',
    items: [
      { path: '/artists', label: 'All Artists' },
      ...roster.map((artist) => ({ path: `/artists#${artist.slug}`, label: artist.name }))
    ]
  },
  {
    label: 'Element 45',
    dropdown: true,
    basePath: '/element45',
    items: [
      { path: '/element45', label: 'Overview' },
      { path: '/element45/olah-bliss', label: 'Òlah Bliss' }
    ]
  },
  { path: '/store', label: 'Store' }
];

const DesktopNav = ({ navLinks, openDropdown, setOpenDropdown, location }) => (
  <nav className="hidden md:flex items-center space-x-8">
    {navLinks.map((link) =>
      link.dropdown ? (
        <DesktopDropdown
          key={link.label}
          link={link}
          isOpen={openDropdown === link.label}
          onOpen={() => setOpenDropdown(link.label)}
          onClose={() => setOpenDropdown(null)}
          location={location}
        />
      ) : (
        <Link
          key={link.label}
          to={link.path}
          className={`nav-link text-sm font-medium tracking-wider uppercase transition-all duration-300 ${navLinkClass(location.pathname === link.path)}`}
        >
          {link.label}
        </Link>
      )
    )}
  </nav>
);

const MobileNav = ({ navLinks, openDropdown, setOpenDropdown, onNavigate, location }) => (
  <nav className="md:hidden pt-6 pb-4 space-y-4 animate-fade-in" data-testid="mobile-nav">
    {navLinks.map((link) =>
      link.dropdown ? (
        <MobileDropdown
          key={link.label}
          link={link}
          isOpen={openDropdown === link.label}
          onToggle={() => setOpenDropdown(openDropdown === link.label ? null : link.label)}
          onNavigate={onNavigate}
          location={location}
        />
      ) : (
        <Link
          key={link.label}
          to={link.path}
          onClick={onNavigate}
          className={`block text-lg font-medium tracking-wider uppercase transition-all duration-300 ${navLinkClass(location.pathname === link.path)}`}
        >
          {link.label}
        </Link>
      )
    )}
  </nav>
);

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();
  const { roster } = useContent();

  const navLinks = buildNavLinks(roster);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-transparent">
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" data-testid="header-logo-link">
            <img
              src="/rhodium-logo.png"
              alt="Rhodium Records"
              className="w-36 md:w-48 h-auto object-contain select-none pointer-events-none transition-transform duration-500 group-hover:scale-105"
              data-testid="header-logo-image"
            />
          </Link>

          <DesktopNav
            navLinks={navLinks}
            openDropdown={openDropdown}
            setOpenDropdown={setOpenDropdown}
            location={location}
          />

          <button
            type="button"
            className="md:hidden text-white/90 hover:text-silver-400 transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {isMobileMenuOpen && (
          <MobileNav
            navLinks={navLinks}
            openDropdown={openDropdown}
            setOpenDropdown={setOpenDropdown}
            onNavigate={() => setIsMobileMenuOpen(false)}
            location={location}
          />
        )}
      </div>
    </header>
  );
};

export default Header;
